/**
 * Composite an overlay PNG (the viewfinder frame) onto a captured photo
 * blob so the saved/shared image includes the frame, not just the live
 * preview. Fits the overlay to the photo's bounds using object-fit:contain
 * math — same as how the live preview renders the frame above the canvas.
 */
export async function compositeFrameOnto(
  base: Blob,
  frameUrl: string,
): Promise<Blob> {
  const [baseBitmap, frameBitmap] = await Promise.all([
    createImageBitmap(base),
    fetch(frameUrl)
      .then((r) => r.blob())
      .then((b) => createImageBitmap(b)),
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = baseBitmap.width;
  canvas.height = baseBitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');
  ctx.drawImage(baseBitmap, 0, 0);

  // object-fit: contain — scale so the frame fits inside, preserving aspect.
  const baseAR = baseBitmap.width / baseBitmap.height;
  const frameAR = frameBitmap.width / frameBitmap.height;
  let dw: number;
  let dh: number;
  if (frameAR > baseAR) {
    // Frame wider than base — fit by width.
    dw = baseBitmap.width;
    dh = baseBitmap.width / frameAR;
  } else {
    dh = baseBitmap.height;
    dw = baseBitmap.height * frameAR;
  }
  const dx = (baseBitmap.width - dw) / 2;
  const dy = (baseBitmap.height - dh) / 2;
  ctx.drawImage(frameBitmap, dx, dy, dw, dh);

  baseBitmap.close();
  frameBitmap.close();

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
      base.type === 'image/png' ? 'image/png' : 'image/jpeg',
      0.92,
    );
  });
}
